import { Descriptions, Divider, Grid } from 'antd';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type ConsentsAndDeclarationsModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/ConsentsAndDeclarationsModuleWithPermissionsSpecs';
import { useListConsentsAndDeclarationsQuery } from '../../../../api/queries/listConsentsAndDeclarations';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import useListReducer from '../../ConsentsAndDeclarationsListPage/useListReducer';
import ConsentsAndDeclarationsModuleEmptyList from './ConsentsAndDeclarationsModuleEmptyList';
import ConsentsAndDeclarationsModuleTable from './ConsentsAndDeclarationsModuleTable';
import ConsentsAndDeclarationsModuleActions from './ConsentsModuleUpdateDrawer';

export type ConsentsAndDeclarationsModulePageProps = {
    module: ConsentsAndDeclarationsModuleWithPermissionsSpecsFragment;
};

const ConsentsAndDeclarationsModulePage = ({ module }: ConsentsAndDeclarationsModulePageProps) => {
    const { t } = useTranslation(['consentsAndDeclarationsModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const [state, dispatch] = useListReducer();
    const { page, pageSize, sort } = state;

    const { data } = useListConsentsAndDeclarationsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { moduleId: module.id },
        },
    });

    // prepare items as data source
    const dataSource = useMemo(
        () => (data?.consentsAndDeclarations?.items || []).map(item => ({ ...item, key: item.id })),
        [data]
    );

    const total = data?.consentsAndDeclarations?.count || 0;

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('consentsAndDeclarationsModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('consentsAndDeclarationsModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<ConsentsAndDeclarationsModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md
                    ? t('consentsAndDeclarationsModuleDetails:title', { name: module.displayName })
                    : module.displayName
            }
        >
            <Divider>{t('consentsAndDeclarationsModuleDetails:dividers.consentsAndDeclarations')}</Divider>
            {total === 0 ? (
                <ConsentsAndDeclarationsModuleEmptyList module={module} />
            ) : (
                <ConsentsAndDeclarationsModuleTable
                    dataSource={dataSource}
                    dispatch={dispatch}
                    module={module}
                    state={state}
                    total={total}
                />
            )}
        </ConsolePageWithHeader>
    );
};

export default ConsentsAndDeclarationsModulePage;
